import { ReactNode } from 'react';
import { Input as AntInput } from 'antd';
import styles from './Input.module.css';

interface InputProps {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  type?: string;
  value?: string | number | readonly string[];
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  name?: string;
}

const sizeMap = {
  sm: 'small' as const,
  md: 'middle' as const,
  lg: 'large' as const,
};

export function Input({
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  size = 'md',
  fullWidth = false,
  className,
  id,
  disabled,
  required,
  type,
  value,
  onChange,
  placeholder,
  name,
}: InputProps) {
  const inputId = id || name;
  const helperText = error || hint;

  const commonProps = {
    id: inputId,
    name,
    status: error ? 'error' as const : undefined,
    disabled,
    size: sizeMap[size],
    className,
    prefix: leftIcon || undefined,
    suffix: rightIcon || undefined,
    placeholder,
    value: value as string,
    onChange,
    style: fullWidth ? { width: '100%' } : undefined,
  };

  return (
    <div className={fullWidth ? styles.fullWidth : undefined}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      {type === 'password' ? (
        <AntInput.Password {...commonProps} />
      ) : (
        <AntInput {...commonProps} type={type} />
      )}
      {helperText && (
        <div
          id={error ? `${inputId}-error` : `${inputId}-hint`}
          className={error ? styles.helperError : styles.helperHint}
        >
          {helperText}
        </div>
      )}
    </div>
  );
}

Input.displayName = 'Input';
